/**
 * Smooth open/close animation for <details> accordions.
 * Animates the element's actual height via the Web Animations API instead of
 * relying on CSS-only tricks (grid-template-rows / max-height), which cannot
 * reliably animate the native <details> open/close in all browsers.
 */

const DURATION = 300;
const EASING = 'ease-out';

const instances = new WeakMap();

class AnimatedDetails {
    constructor(details) {
        this.details = details;
        this.summary = details.querySelector(':scope > summary');
        this.content = details.querySelector(':scope > .details__content, :scope > .details__content-wrapper');

        if (!this.summary || !this.content) {
            return;
        }

        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.summary.addEventListener('click', (event) => this.onClick(event));
    }

    onClick(event) {
        event.preventDefault();

        if (this.reduceMotion) {
            this.details.open = !this.details.open;
            return;
        }

        // Animate the content wrapper's own height, not the <details> element's —
        // some accordions (e.g. IconCardsElement cards) put a box-shadow on the
        // <details> itself, and overflow:hidden on that element would clip it.
        this.content.style.overflow = 'hidden';

        if (this.isClosing || !this.details.open) {
            this.open();
        } else if (this.isExpanding || this.details.open) {
            this.shrink();
        }
    }

    shrink() {
        this.isClosing = true;

        const startHeight = `${this.content.offsetHeight}px`;
        const endHeight = '0px';

        if (this.animation) {
            this.animation.cancel();
        }

        this.animation = this.content.animate(
            { height: [startHeight, endHeight] },
            { duration: DURATION, easing: EASING, fill: 'forwards' }
        );

        this.animation.onfinish = () => this.onAnimationFinish(false);
        this.animation.oncancel = () => { this.isClosing = false; };
    }

    open() {
        this.content.style.height = `${this.content.offsetHeight}px`;
        this.details.open = true;

        window.requestAnimationFrame(() => this.grow());
    }

    grow() {
        this.isExpanding = true;

        const startHeight = `${this.content.offsetHeight}px`;
        const endHeight = `${this.content.scrollHeight}px`;

        if (this.animation) {
            this.animation.cancel();
        }

        this.animation = this.content.animate(
            { height: [startHeight, endHeight] },
            { duration: DURATION, easing: EASING, fill: 'forwards' }
        );

        this.animation.onfinish = () => this.onAnimationFinish(true);
        this.animation.oncancel = () => { this.isExpanding = false; };
    }

    onAnimationFinish(open) {
        this.details.open = open;

        // Release the fill:'forwards' hold explicitly — otherwise the finished
        // animation keeps pinning height indefinitely and skews the next
        // open()/shrink() measurement.
        if (this.animation) {
            this.animation.cancel();
        }
        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;
        this.content.style.height = '';
        this.content.style.overflow = '';
    }
}

/**
 * Wires up smooth animation for a single <details> element. Safe to call
 * multiple times on the same element (e.g. from Vue re-renders).
 */
export function initAccordion(details) {
    if (!details) {
        return undefined;
    }
    if (!details.dataset.accordionAnimated) {
        details.dataset.accordionAnimated = 'true';
        instances.set(details, new AnimatedDetails(details));
    }
    return instances.get(details);
}

export function initAccordions(root = document) {
    root.querySelectorAll('details.spec-group, details.animated-details').forEach(initAccordion);
}

/**
 * Programmatically open/close a <details> through the same animated path a
 * summary click would use (instead of a hard `details.open = …` cut). Used
 * by bulk actions like an "expand/collapse all" button.
 */
export function setAccordionOpen(details, open) {
    const instance = initAccordion(details);

    if (!instance || !instance.summary || !instance.content || instance.reduceMotion) {
        details.open = open;
        return;
    }

    if (details.open === open && !instance.isClosing && !instance.isExpanding) {
        return;
    }

    instance.content.style.overflow = 'hidden';

    if (open) {
        instance.open();
    } else {
        instance.shrink();
    }
}

document.addEventListener('DOMContentLoaded', () => initAccordions());
